import { authHeaders as sharedAuthHeaders } from "../lib/http";
import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Layers,
  Plus,
  Trash2,
  X,
  Save,
  Loader2,
  Copy,
  Search,
  Sparkles,
  Store,
  TrendingUp,
  Star,
  BarChart3,
  Filter,
  ArrowUpDown,
} from "lucide-react";

const API_BASE = import.meta.env.VITE_API_URL || "";

// Aktif magaza (X-Shop-Id) dahil ortak basliklar: kayitlar dogru magazaya yazilsin
const authHeaders = () => sharedAuthHeaders();

// ======== Types ========

interface Concept {
  id: string;
  name: string;
  description: string | null;
  emoji: string | null;
  color: string | null;
  season: string | null;
  keywords: string[];
  audiences: string[];
  occasions: string[];
  styles: string[];
  negativeKeywords: string[];
  defaultSectionId: string | null;
  defaultSectionTitle: string | null;
  isActive: boolean;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

interface ShopSection {
  shop_section_id: number;
  title: string;
  active_listing_count: number;
}

type ConceptForm = Omit<Concept, "id" | "usageCount" | "createdAt" | "updatedAt">;

const EMPTY_FORM: ConceptForm = {
  name: "",
  description: "",
  emoji: "",
  color: "#6366f1",
  season: "",
  keywords: [],
  audiences: [],
  occasions: [],
  styles: [],
  negativeKeywords: [],
  defaultSectionId: null,
  defaultSectionTitle: null,
  isActive: true,
};

// Keyword grup tanimlari (etiket + ipucu + prompt'taki rol)
const KEYWORD_GROUPS: Array<{
  key: keyof Pick<ConceptForm, "keywords" | "audiences" | "occasions" | "styles" | "negativeKeywords">;
  label: string;
  hint: string;
  accent: string;
}> = [
  { key: "keywords", label: "Anahtar Kelimeler", hint: "Ana havuz — AI kısa olanları başlıkta, uzun (2-4 kelime) olanları etikette kullanır", accent: "indigo" },
  { key: "audiences", label: "Hedef kitle", hint: "Alıcı / hediye edilen kişi (teachers, moms...)", accent: "sky" },
  { key: "occasions", label: "Occasion / olay", hint: "Hediye/etkinlik açıları (first day, graduation...)", accent: "emerald" },
  { key: "styles", label: "Stil", hint: "Görsel/tasarım açıları (retro, minimalist...)", accent: "amber" },
  { key: "negativeKeywords", label: "Negatif (kaçınılacak)", hint: "AI bunları ASLA kullanmaz (off-theme)", accent: "rose" },
];

const ACCENT_CLASSES: Record<string, string> = {
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  violet: "bg-violet-50 text-violet-700 border-violet-200",
  sky: "bg-sky-50 text-sky-700 border-sky-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200",
};

// ======== Hazir konsept sablonlari ========
const PRESETS: Array<Partial<ConceptForm> & { name: string; emoji: string; color: string }> = [
  {
    name: "Back to School", emoji: "🎒", color: "#f59e0b", season: "Ağustos–Eylül",
    description: "Okula dönüş temalı; öğretmen ve öğrencilere yönelik tasarımlar.",
    keywords: ["back to school", "teacher shirt", "first day of school", "teacher appreciation", "classroom decor", "student gift", "back to school gift"],
    audiences: ["teachers", "students", "moms", "kindergarten"],
    occasions: ["first day of school", "graduation", "teacher appreciation week"],
    styles: ["retro", "cute", "minimalist"],
    negativeKeywords: ["halloween", "christmas"],
  },
  {
    name: "Christmas", emoji: "🎄", color: "#dc2626", season: "Kasım–Aralık",
    description: "Noel ve yılbaşı temalı hediye tasarımları.",
    keywords: ["christmas shirt", "christmas gift", "holiday sweater", "family christmas", "matching pajamas", "secret santa gift", "christmas party"],
    audiences: ["family", "kids", "grandma", "coworkers"],
    occasions: ["christmas eve", "holiday party", "stocking stuffer"],
    styles: ["vintage", "cozy", "funny"],
    negativeKeywords: ["halloween", "back to school"],
  },
  {
    name: "Halloween", emoji: "🎃", color: "#ea580c", season: "Ekim",
    description: "Cadılar Bayramı temalı ürkütücü/eğlenceli tasarımlar.",
    keywords: ["halloween shirt", "spooky season", "halloween costume", "trick or treat", "pumpkin patch", "spooky vibes", "ghost design"],
    audiences: ["kids", "teens", "party goers"],
    occasions: ["halloween party", "trick or treating"],
    styles: ["spooky", "cute", "retro"],
    negativeKeywords: ["christmas", "valentine"],
  },
  {
    name: "Valentine's Day", emoji: "❤️", color: "#e11d48", season: "Şubat",
    description: "Sevgililer Günü temalı romantik hediye tasarımları.",
    keywords: ["valentines shirt", "valentines gift", "love shirt", "gift for her", "gift for him", "couple matching", "galentines day"],
    audiences: ["couples", "girlfriend", "boyfriend", "best friend"],
    occasions: ["valentines day", "anniversary", "date night"],
    styles: ["romantic", "minimalist", "funny"],
    negativeKeywords: ["halloween", "christmas"],
  },
];

// ======== API helpers ========
async function fetchConcepts(): Promise<Concept[]> {
  const res = await fetch(`${API_BASE}/api/concepts`, { headers: authHeaders() });
  if (!res.ok) return [];
  const json = await res.json();
  return json.data ?? [];
}

async function fetchShopSections(): Promise<ShopSection[]> {
  try {
    const res = await fetch(`${API_BASE}/api/etsy/listings/shop-sections/list`, { headers: authHeaders() });
    if (!res.ok) return [];
    const json = await res.json();
    return json.data?.results ?? json.results ?? [];
  } catch {
    return [];
  }
}

// ======== Chip editor ========
function ChipInput({
  values,
  onChange,
  accent,
  placeholder,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  accent: string;
  placeholder: string;
}) {
  const [draft, setDraft] = useState("");

  function commit(raw: string) {
    // virgul ile ayrilan coklu girisi destekle
    const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
    if (parts.length === 0) return;
    const next = [...values];
    for (const p of parts) {
      if (!next.some((v) => v.toLowerCase() === p.toLowerCase())) next.push(p);
    }
    onChange(next);
    setDraft("");
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-gray-200 bg-white p-2 focus-within:ring-2 focus-within:ring-indigo-200">
      {values.map((v, i) => (
        <span key={`${v}-${i}`} className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium ${ACCENT_CLASSES[accent]}`}>
          {v}
          <button type="button" onClick={() => onChange(values.filter((_, idx) => idx !== i))} className="opacity-60 hover:opacity-100">
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            commit(draft);
          } else if (e.key === "Backspace" && !draft && values.length) {
            onChange(values.slice(0, -1));
          }
        }}
        onBlur={() => draft && commit(draft)}
        placeholder={values.length === 0 ? placeholder : ""}
        className="min-w-[120px] flex-1 border-none bg-transparent px-1 py-0.5 text-sm outline-none"
      />
    </div>
  );
}

// ======== eRank Keyword Analyzer ========

type GroupKey = "keywords" | "audiences" | "occasions" | "styles" | "negativeKeywords";

interface KeywordRow {
  keyword: string;
  chars: number;
  searches: number | null;
  clicks: number | null;
  kd: number | null;
  ctr: number | null;
  competition: number | null;
  google: number | null;
  score: number;
  star: boolean;
}

function toNum(s: string | undefined): number | null {
  if (s == null) return null;
  const t = String(s).replace(/,/g, "").replace(/%/g, "").trim();
  if (!t || /^n\/?a$/i.test(t)) return null;
  const n = Number(t);
  return Number.isNaN(n) ? null : n;
}

// eRank tablosunu (Tab ayrac) veya "Copy all tags" (virgul) metnini satirlara cevirir
function parseErank(raw: string): Array<Omit<KeywordRow, "score" | "star">> {
  const text = raw.trim();
  if (!text) return [];

  const hasTab = text.includes("\t");
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

  // "Copy all tags" tek satir virgul listesi (tab yok, tek satir, virgul var)
  if (!hasTab && lines.length <= 3 && text.includes(",")) {
    const kws = text.replace(/^.*?:/, "").split(",").map((k) => k.trim()).filter(Boolean);
    return kws.map((k) => ({ keyword: k, chars: k.length, searches: null, clicks: null, kd: null, ctr: null, competition: null, google: null }));
  }

  const split = (l: string) => (hasTab ? l.split("\t") : l.split(/\s{2,}/));

  // Baslik satiri var mi?
  const first = lines[0]?.toLowerCase() ?? "";
  const hasHeader = first.includes("keyword") && (first.includes("search") || first.includes("competition"));

  let idx = { keyword: 0, chars: 1, searches: 3, clicks: 4, kd: 5, ctr: 6, competition: 7, google: 8 };
  let body = lines;
  if (hasHeader) {
    const cols = split(lines[0]).map((c) => c.toLowerCase().trim());
    const find = (pred: (c: string) => boolean, fallback: number) => {
      const i = cols.findIndex(pred);
      return i >= 0 ? i : fallback;
    };
    idx = {
      keyword: find((c) => c.includes("keyword"), 0),
      chars: find((c) => c.includes("char"), 1),
      searches: find((c) => c.includes("average") && c.includes("search"), 3),
      clicks: find((c) => c.includes("click"), 4),
      kd: find((c) => c === "kd" || c.includes("difficulty"), 5),
      ctr: find((c) => c.includes("ctr"), 6),
      competition: find((c) => c.includes("competition"), 7),
      google: find((c) => c.includes("google"), 8),
    };
    body = lines.slice(1);
  }

  const out: Array<Omit<KeywordRow, "score" | "star">> = [];
  for (const line of body) {
    const cells = split(line);
    const keyword = (cells[idx.keyword] ?? "").trim();
    if (!keyword || /^keyword/i.test(keyword)) continue;
    const charsCell = toNum(cells[idx.chars]);
    out.push({
      keyword,
      chars: charsCell != null ? charsCell : keyword.length,
      searches: toNum(cells[idx.searches]),
      clicks: toNum(cells[idx.clicks]),
      kd: toNum(cells[idx.kd]),
      ctr: toNum(cells[idx.ctr]),
      competition: toNum(cells[idx.competition]),
      google: toNum(cells[idx.google]),
    });
  }
  // Tekilleştir (keyword bazli)
  const seen = new Set<string>();
  return out.filter((r) => {
    const k = r.keyword.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

// Firsat skoru: yuksek arama + dusuk rekabet + dusuk KD (0-100, dataset ici rank-bazli)
function scoreRows(rows: Array<Omit<KeywordRow, "score" | "star">>): KeywordRow[] {
  const sVals = rows.map((r) => r.searches).filter((v): v is number => v != null && v > 0).sort((a, b) => a - b);
  const cVals = rows.map((r) => r.competition).filter((v): v is number => v != null).sort((a, b) => a - b);
  const pct = (sorted: number[], v: number | null): number => {
    if (v == null || sorted.length === 0) return 0.5;
    let count = 0;
    for (const x of sorted) { if (x <= v) count++; else break; }
    return count / sorted.length;
  };
  // Star = mutlak firsat kurali: yeterli talep + dusuk rekabet + asiri zor degil
  const STAR_MIN_SEARCH = 40;
  const STAR_MAX_COMP = 5000;
  const STAR_MAX_KD = 85;
  return rows.map((r) => {
    const sRank = r.searches != null && r.searches > 0 ? pct(sVals, r.searches) : 0;
    const cRank = r.competition == null ? 0.5 : 1 - pct(cVals, r.competition);
    const kdComp = r.kd == null ? 0.5 : (100 - Math.max(0, Math.min(100, r.kd))) / 100;
    // Rekabete biraz daha agirlik (kullanici oncelikli: dusuk rekabet)
    const score = Math.round(100 * (0.45 * cRank + 0.4 * sRank + 0.15 * kdComp));
    const star =
      (r.searches ?? 0) >= STAR_MIN_SEARCH &&
      r.competition != null &&
      r.competition <= STAR_MAX_COMP &&
      (r.kd == null || r.kd <= STAR_MAX_KD);
    return { ...r, score, star };
  });
}

type SortKey = "score" | "searches" | "competition" | "kd" | "ctr" | "chars";

function KeywordAnalyzer({
  onClose,
  onAdd,
  existing,
  removed,
  onRemove,
}: {
  onClose: () => void;
  onAdd: (group: GroupKey, keywords: string[]) => void;
  existing: Record<GroupKey, string[]>;
  removed: Set<string>;
  onRemove: (keyword: string) => void;
}) {
  const [raw, setRaw] = useState("");
  const [rows, setRows] = useState<KeywordRow[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [targetGroup, setTargetGroup] = useState<GroupKey>("keywords");
  const [sortKey, setSortKey] = useState<SortKey>("score");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  // Filtreler
  const [minSearch, setMinSearch] = useState(40);
  const [maxComp, setMaxComp] = useState(50000);
  const [maxKd, setMaxKd] = useState(100);
  const [tagOnly, setTagOnly] = useState(false);
  const [starOnly, setStarOnly] = useState(false);

  function analyze() {
    const parsed = parseErank(raw);
    setRows(scoreRows(parsed));
    setSelected(new Set());
  }

  const hasMetrics = useMemo(() => rows.some((r) => r.searches != null || r.competition != null), [rows]);

  const filtered = useMemo(() => {
    let r = rows.filter((row) => {
      // Metrik bazli filtreler yalnizca dataset metrik iceriyorsa uygulanir
      if (hasMetrics) {
        if ((row.searches ?? 0) < minSearch) return false;
        if (row.competition != null && row.competition > maxComp) return false;
        if (row.kd != null && row.kd > maxKd) return false;
        if (starOnly && !row.star) return false;
      }
      if (tagOnly && row.chars > 20) return false;
      return true;
    });
    const dir = sortDir === "asc" ? 1 : -1;
    r = [...r].sort((a, b) => {
      const av = (a[sortKey] ?? -1) as number;
      const bv = (b[sortKey] ?? -1) as number;
      return (av - bv) * dir;
    });
    return r;
  }, [rows, hasMetrics, minSearch, maxComp, maxKd, tagOnly, starOnly, sortKey, sortDir]);

  // Hedef grupta zaten olan (ekli) veya daha once cikarilan kelimeler eklenemez
  const existingSet = useMemo(() => new Set(existing[targetGroup].map((k) => k.toLowerCase())), [existing, targetGroup]);
  const statusOf = (kw: string): "ok" | "exists" | "removed" => {
    const l = kw.toLowerCase();
    if (existingSet.has(l)) return "exists";
    if (removed.has(l)) return "removed";
    return "ok";
  };
  const selectableCount = useMemo(() => filtered.filter((r) => statusOf(r.keyword) === "ok").length, [filtered, existingSet, removed]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("desc"); }
  }

  function toggle(k: string) {
    if (statusOf(k) !== "ok") return; // ekli/cikarilmis secilemez
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  }

  function removeRow(k: string) {
    onRemove(k); // editor hafizasina "cikarildi" kaydi
    setRows((prev) => prev.filter((r) => r.keyword !== k));
    setSelected((prev) => { const n = new Set(prev); n.delete(k); return n; });
  }

  function selectAllVisible() {
    setSelected(new Set(filtered.filter((r) => statusOf(r.keyword) === "ok").map((r) => r.keyword)));
  }
  function selectStars() {
    setSelected(new Set(filtered.filter((r) => r.star && statusOf(r.keyword) === "ok").map((r) => r.keyword)));
  }

  function handleAdd() {
    const kws = filtered.filter((r) => selected.has(r.keyword) && statusOf(r.keyword) === "ok").map((r) => r.keyword);
    if (kws.length) onAdd(targetGroup, kws);
    setSelected(new Set()); // eklenenler artik "ekli" olur; panel acik kalir
  }

  const fmt = (n: number | null) => (n == null ? "—" : n.toLocaleString("en-US"));
  const scoreColor = (s: number) => (s >= 75 ? "bg-emerald-100 text-emerald-700" : s >= 55 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500");
  const compColor = (c: number | null) => (c == null ? "text-gray-400" : c <= 5000 ? "text-emerald-600" : c <= 50000 ? "text-amber-600" : "text-rose-600");

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div className="my-6 w-full max-w-5xl rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h2 className="flex items-center gap-2 text-base font-bold text-gray-900">
            <BarChart3 className="h-5 w-5 text-indigo-600" /> eRank Fırsat Analizi
          </h2>
          <button onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>

        <div className="max-h-[78vh] overflow-y-auto px-6 py-5">
          {rows.length === 0 ? (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">
                eRank tablosunu kopyalayıp (başlık satırı dahil, Tab ayraçlı) veya "Copy all tags" listesini aşağıya yapıştır. Yüksek arama + düşük rekabet olan fırsat kelimeleri skorlanır.
              </p>
              <textarea
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                rows={10}
                placeholder={"Keywords\tCharacter Count\tTag Occurrences\tAverage Searches\t...\nback to school\t14\t44\t9961\t..."}
                className="w-full resize-y rounded-lg border border-gray-200 p-3 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
              <div className="flex justify-end">
                <button onClick={analyze} disabled={!raw.trim()}
                  className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
                  <BarChart3 className="h-4 w-4" /> Analiz Et
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {!hasMetrics && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-700">
                  Bu bir etiket listesi (metrik yok) — fırsat skoru hesaplanamaz, kelimeler yalnızca seçmen için listelendi. Tam fırsat analizi (arama/rekabet/KD) için eRank tablosunu <b>sütun başlıklarıyla</b> kopyalayıp yapıştır.
                </div>
              )}
              {/* Filtreler */}
              <div className="flex flex-wrap items-end gap-3 rounded-xl border border-gray-100 bg-gray-50/60 p-3">
                <span className="flex items-center gap-1 text-[11px] font-semibold text-gray-500"><Filter className="h-3 w-3" /> Filtreler</span>
                {hasMetrics && (
                  <>
                    <label className="text-[11px] text-gray-600">Min arama
                      <input type="number" value={minSearch} onChange={(e) => setMinSearch(Number(e.target.value) || 0)}
                        className="mt-0.5 block w-20 rounded border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-200" />
                    </label>
                    <label className="text-[11px] text-gray-600">Max rekabet
                      <input type="number" value={maxComp} onChange={(e) => setMaxComp(Number(e.target.value) || 0)}
                        className="mt-0.5 block w-24 rounded border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-200" />
                    </label>
                    <label className="text-[11px] text-gray-600">Max KD
                      <input type="number" value={maxKd} onChange={(e) => setMaxKd(Number(e.target.value) || 0)}
                        className="mt-0.5 block w-16 rounded border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-200" />
                    </label>
                  </>
                )}
                <label className="flex items-center gap-1 text-[11px] text-gray-600"><input type="checkbox" checked={tagOnly} onChange={(e) => setTagOnly(e.target.checked)} className="h-3 w-3" /> Tag uyumlu (≤20 kar)</label>
                <label className="flex items-center gap-1 text-[11px] text-gray-600"><input type="checkbox" checked={starOnly} onChange={(e) => setStarOnly(e.target.checked)} className="h-3 w-3" /> Sadece ⭐ fırsatlar</label>
                <button onClick={() => { setRows([]); setRaw(""); }} className="ml-auto text-[11px] text-gray-400 hover:text-gray-600">↺ Yeni veri</button>
              </div>

              {/* Sec kisayollari */}
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-500">{filtered.length} kelime · {selectableCount} eklenebilir · {selected.size} seçili</span>
                {hasMetrics && <button onClick={selectStars} className="rounded border border-emerald-200 bg-emerald-50 px-2 py-1 text-emerald-700 hover:bg-emerald-100">⭐ Fırsatları seç</button>}
                <button onClick={selectAllVisible} className="rounded border border-gray-200 px-2 py-1 text-gray-600 hover:bg-gray-50">Tümünü seç</button>
                {selected.size > 0 && <button onClick={() => setSelected(new Set())} className="text-gray-400 hover:text-gray-600">Temizle</button>}
              </div>

              {/* Tablo */}
              <div className="overflow-x-auto rounded-xl border border-gray-100">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="w-8 px-2 py-2"></th>
                      <th className="px-2 py-2 text-left">Keyword</th>
                      {([["score", "Skor"], ["searches", "Arama"], ["competition", "Rekabet"], ["kd", "KD"], ["ctr", "CTR"], ["chars", "Kar."]] as [SortKey, string][]).map(([k, label]) => (
                        <th key={k} className="cursor-pointer px-2 py-2 text-right hover:text-gray-700" onClick={() => toggleSort(k)}>
                          <span className="inline-flex items-center gap-0.5">{label}<ArrowUpDown className="h-2.5 w-2.5 opacity-40" /></span>
                        </th>
                      ))}
                      <th className="w-8 px-2 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => {
                      const status = statusOf(r.keyword);
                      const blocked = status !== "ok";
                      const isSel = selected.has(r.keyword);
                      return (
                        <tr key={r.keyword} onClick={() => toggle(r.keyword)}
                          className={`border-t border-gray-50 ${blocked ? "bg-gray-50/60 text-gray-400" : isSel ? "cursor-pointer bg-indigo-50" : "cursor-pointer hover:bg-gray-50"}`}>
                          <td className="px-2 py-1.5 text-center"><input type="checkbox" checked={isSel} disabled={blocked} readOnly className="h-3 w-3 pointer-events-none" /></td>
                          <td className="px-2 py-1.5">
                            <span className="inline-flex items-center gap-1">
                              {r.star && !blocked && <Star className="h-3 w-3 fill-amber-400 text-amber-400" />}
                              <span className={blocked ? "line-through" : r.chars <= 20 ? "text-gray-900" : "text-gray-500"}>{r.keyword}</span>
                              {status === "exists" && <span className="rounded bg-gray-200 px-1 py-0.5 text-[9px] text-gray-500">ekli</span>}
                              {status === "removed" && <span className="rounded bg-rose-100 px-1 py-0.5 text-[9px] text-rose-500">daha önce çıkarıldı</span>}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-right">{hasMetrics ? <span className={`rounded px-1.5 py-0.5 font-semibold ${scoreColor(r.score)}`}>{r.score}</span> : <span className="text-gray-300">—</span>}</td>
                          <td className="px-2 py-1.5 text-right text-gray-700">{fmt(r.searches)}</td>
                          <td className={`px-2 py-1.5 text-right font-medium ${compColor(r.competition)}`}>{fmt(r.competition)}</td>
                          <td className="px-2 py-1.5 text-right text-gray-500">{r.kd ?? "—"}</td>
                          <td className="px-2 py-1.5 text-right text-gray-500">{r.ctr != null ? `${r.ctr}%` : "—"}</td>
                          <td className={`px-2 py-1.5 text-right ${r.chars <= 20 ? "text-emerald-600" : "text-rose-500"}`}>{r.chars}</td>
                          <td className="px-1 py-1.5 text-center">
                            {status !== "removed" && (
                              <button onClick={(e) => { e.stopPropagation(); removeRow(r.keyword); }} title="Listeden çıkar" className="rounded p-0.5 text-gray-300 hover:bg-rose-50 hover:text-rose-500">
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {filtered.length === 0 && <p className="py-6 text-center text-xs text-gray-400">Filtrelere uyan kelime yok — eşikleri gevşet.</p>}
              </div>
            </div>
          )}
        </div>

        {rows.length > 0 && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 px-6 py-4">
            <span className="mr-auto text-[11px] text-gray-400">Kar. yeşil = Etsy tag limiti (≤20). Rekabet yeşil = düşük.</span>
            <span className="text-xs text-gray-600">Eklenecek grup:</span>
            <select value={targetGroup} onChange={(e) => setTargetGroup(e.target.value as GroupKey)}
              className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200">
              {KEYWORD_GROUPS.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
            </select>
            <button onClick={handleAdd} disabled={selected.size === 0}
              className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
              <Plus className="h-4 w-4" /> {selected.size} kelimeyi ekle
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ======== Editor modal ========
function ConceptEditor({
  initial,
  sections,
  saving,
  onCancel,
  onSave,
  onCreateSection,
}: {
  initial: ConceptForm;
  sections: ShopSection[];
  saving: boolean;
  onCancel: () => void;
  onSave: (form: ConceptForm) => void;
  onCreateSection: (title: string) => Promise<ShopSection | null>;
}) {
  const [form, setForm] = useState<ConceptForm>(initial);
  useEffect(() => setForm(initial), [initial]);

  const [creatingSection, setCreatingSection] = useState(false);
  const [sectionErr, setSectionErr] = useState<string | null>(null);
  const [showAnalyzer, setShowAnalyzer] = useState(false);
  const [removedKeywords, setRemovedKeywords] = useState<Set<string>>(new Set());
  const [aiLoading, setAiLoading] = useState<Record<string, boolean>>({});
  const [aiErr, setAiErr] = useState<string | null>(null);

  function set<K extends keyof ConceptForm>(key: K, val: ConceptForm[K]) {
    setForm((prev) => ({ ...prev, [key]: val }));
  }

  // Analiz/AI'dan gelen kelimeleri ilgili gruba tekilleştirerek ekle (çıkarılanları atla)
  function addKeywordsToGroup(group: GroupKey, keywords: string[]) {
    setForm((prev) => {
      const existing = prev[group];
      const merged = [...existing];
      for (const k of keywords) {
        const t = k.trim();
        if (!t) continue;
        if (removedKeywords.has(t.toLowerCase())) continue;
        if (!merged.some((v) => v.toLowerCase() === t.toLowerCase())) merged.push(t);
      }
      return { ...prev, [group]: merged };
    });
  }

  // AI ile alan önerisi — keyword gruplarına ekler, meta'yı yalnızca boşsa doldurur
  async function suggest(fields: string[], loadingKey: string) {
    if (!form.name.trim()) return;
    setAiLoading((p) => ({ ...p, [loadingKey]: true }));
    setAiErr(null);
    try {
      const res = await fetch(`${API_BASE}/api/concepts/ai-suggest`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ name: form.name.trim(), description: form.description || undefined, fields }),
      });
      const json = await res.json();
      if (!res.ok) { setAiErr(json.error || "AI önerisi başarısız"); return; }
      const data = json.data || {};
      const groupKeys: GroupKey[] = ["keywords", "audiences", "occasions", "styles", "negativeKeywords"];
      for (const g of groupKeys) if (Array.isArray(data[g])) addKeywordsToGroup(g, data[g]);
      setForm((prev) => {
        const next = { ...prev };
        if (typeof data.description === "string" && data.description && !next.description) next.description = data.description;
        if (typeof data.emoji === "string" && data.emoji && !next.emoji) next.emoji = data.emoji;
        if (typeof data.season === "string" && data.season && !next.season) next.season = data.season;
        return next;
      });
    } catch {
      setAiErr("AI bağlantı hatası");
    } finally {
      setAiLoading((p) => ({ ...p, [loadingKey]: false }));
    }
  }
  const ALL_AI_FIELDS = ["keywords", "audiences", "occasions", "styles", "negativeKeywords", "description", "emoji", "season"];

  async function handleCreateSection() {
    const title = (form.defaultSectionTitle || "").trim();
    if (!title) return;
    setCreatingSection(true);
    setSectionErr(null);
    try {
      const created = await onCreateSection(title);
      if (created) {
        set("defaultSectionId", String(created.shop_section_id));
        set("defaultSectionTitle", created.title);
      }
    } catch (e) {
      setSectionErr(e instanceof Error ? e.message : "Bölüm oluşturulamadı");
    } finally {
      setCreatingSection(false);
    }
  }

  const totalKeywords = KEYWORD_GROUPS.reduce((s, g) => s + form[g.key].length, 0);

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="my-8 w-full max-w-3xl rounded-2xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h2 className="flex items-center gap-2 text-base font-bold text-gray-900">
            <Layers className="h-5 w-5 text-indigo-600" />
            {initial.name ? "Konsepti Düzenle" : "Yeni Konsept"}
          </h2>
          <button onClick={onCancel} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[70vh] space-y-5 overflow-y-auto px-6 py-5">
          {/* AI toplu öneri */}
          <div className="flex items-center justify-between rounded-xl border border-indigo-100 bg-indigo-50/50 px-3 py-2">
            <p className="text-[11px] text-gray-500">Konsept adına göre AI tüm alanları önersin (boş meta alanları dolar, keyword grupları genişler).</p>
            <button
              type="button"
              onClick={() => suggest(ALL_AI_FIELDS, "all")}
              disabled={!form.name.trim() || aiLoading.all}
              title={!form.name.trim() ? "Önce konsept adını gir" : "Tüm alanları AI ile öner"}
              className="flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {aiLoading.all ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Tümünü Öner
            </button>
          </div>
          {aiErr && <p className="-mt-3 text-[11px] text-red-500">{aiErr}</p>}

          {/* Temel bilgiler */}
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-2">
              <label className="mb-1 block text-xs font-medium text-gray-700">Emoji</label>
              <input value={form.emoji ?? ""} onChange={(e) => set("emoji", e.target.value)} placeholder="🎒"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-center text-lg focus:outline-none focus:ring-2 focus:ring-indigo-200" />
            </div>
            <div className="col-span-7">
              <label className="mb-1 block text-xs font-medium text-gray-700">Konsept Adı *</label>
              <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Back to School"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
            </div>
            <div className="col-span-3">
              <label className="mb-1 block text-xs font-medium text-gray-700">Renk</label>
              <input type="color" value={form.color ?? "#6366f1"} onChange={(e) => set("color", e.target.value)}
                className="h-[38px] w-full cursor-pointer rounded-lg border border-gray-200 p-1" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Sezon / Zamanlama</label>
              <input value={form.season ?? ""} onChange={(e) => set("season", e.target.value)} placeholder="Ağustos–Eylül"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
            </div>
            <div>
              <label className="mb-1 flex items-center gap-1 text-xs font-medium text-gray-700"><Store className="h-3 w-3" /> Varsayılan Mağaza Bölümü</label>
              <input
                list="erc-shop-sections"
                value={form.defaultSectionTitle ?? ""}
                onChange={(e) => {
                  const title = e.target.value;
                  const match = sections.find((s) => s.title.toLowerCase() === title.trim().toLowerCase());
                  set("defaultSectionTitle", title.trim() ? title : null);
                  set("defaultSectionId", match ? String(match.shop_section_id) : null);
                  setSectionErr(null);
                }}
                placeholder="Bölüm adı yaz veya listeden seç"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
              <datalist id="erc-shop-sections">
                {sections.map((s) => <option key={s.shop_section_id} value={s.title} />)}
              </datalist>
              {/* Durum satiri */}
              {form.defaultSectionId ? (
                <p className="mt-1 text-[10px] text-emerald-600">✓ Etsy bölümüne bağlı — üretimde otomatik doldurulur.</p>
              ) : form.defaultSectionTitle ? (
                <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px]">
                  <span className="text-amber-600">Bu isimde Etsy bölümü yok. İsim kaydedilir; üretimde eşleşme aranır.</span>
                  <button type="button" onClick={handleCreateSection} disabled={creatingSection}
                    className="flex items-center gap-0.5 rounded border border-indigo-200 bg-white px-1.5 py-0.5 font-medium text-indigo-600 hover:bg-indigo-50 disabled:opacity-40">
                    {creatingSection ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Etsy'de oluştur
                  </button>
                  {sectionErr && <span className="text-red-500">{sectionErr}</span>}
                </div>
              ) : (
                <p className="mt-1 text-[10px] text-gray-400">Boş = otomatik doldurma kapalı.</p>
              )}
            </div>
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs font-medium text-gray-700">Kısa Açıklama / AI Brief</label>
              <button type="button" onClick={() => suggest(["description"], "description")} disabled={!form.name.trim() || aiLoading.description}
                title={!form.name.trim() ? "Önce konsept adını gir" : "AI ile öner"}
                className="flex items-center gap-1 rounded-md border border-indigo-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-indigo-600 hover:bg-indigo-50 disabled:opacity-40">
                {aiLoading.description ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} AI ile Öner
              </button>
            </div>
            <textarea value={form.description ?? ""} onChange={(e) => set("description", e.target.value)} rows={2}
              placeholder="Bu konseptin temasını AI'ya anlatan kısa bir özet…"
              className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
          </div>

          {/* Keyword gruplari */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Anahtar Kelime Grupları</p>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setShowAnalyzer(true)}
                  className="flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-2 py-1 text-[11px] font-medium text-indigo-700 hover:bg-indigo-100">
                  <BarChart3 className="h-3 w-3" /> eRank'ten Analiz Et
                </button>
                <span className="text-[11px] text-gray-400">{totalKeywords} kelime</span>
              </div>
            </div>
            {KEYWORD_GROUPS.map((g) => (
              <div key={g.key}>
                <div className="mb-1 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs font-medium text-gray-700">{g.label}</label>
                    <button type="button" onClick={() => suggest([g.key], g.key)} disabled={!form.name.trim() || aiLoading[g.key]}
                      title={!form.name.trim() ? "Önce konsept adını gir" : "AI ile öner"}
                      className="flex items-center gap-1 rounded-md border border-indigo-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-indigo-600 hover:bg-indigo-50 disabled:opacity-40">
                      {aiLoading[g.key] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} AI
                    </button>
                  </div>
                  <span className="truncate text-[10px] text-gray-400">{g.hint}</span>
                </div>
                <ChipInput
                  values={form[g.key]}
                  onChange={(next) => set(g.key, next)}
                  accent={g.accent}
                  placeholder="Kelime yaz, Enter'a bas (virgülle çoklu ekle)"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-6 py-4">
          <button onClick={onCancel} className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100">İptal</button>
          <button
            onClick={() => onSave(form)}
            disabled={saving || !form.name.trim()}
            className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Kaydet
          </button>
        </div>
      </div>

      {showAnalyzer && (
        <KeywordAnalyzer
          onClose={() => setShowAnalyzer(false)}
          onAdd={(group, keywords) => addKeywordsToGroup(group, keywords)}
          existing={{
            keywords: form.keywords,
            audiences: form.audiences,
            occasions: form.occasions,
            styles: form.styles,
            negativeKeywords: form.negativeKeywords,
          }}
          removed={removedKeywords}
          onRemove={(kw) => setRemovedKeywords((prev) => new Set(prev).add(kw.toLowerCase()))}
        />
      )}
    </div>
  );
}

// ======== Concept card ========
function ConceptCard({
  concept,
  onEdit,
  onDelete,
  onDuplicate,
}: {
  concept: Concept;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
}) {
  const total =
    concept.keywords.length +
    concept.audiences.length +
    concept.occasions.length +
    concept.styles.length;
  const accent = concept.color || "#6366f1";

  return (
    <div className="group relative flex flex-col rounded-xl border border-gray-200 bg-white p-4 transition-shadow hover:shadow-md">
      <div className="mb-2 flex items-start gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-lg" style={{ backgroundColor: `${accent}1a` }}>
          {concept.emoji || "🎨"}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-gray-900">{concept.name}</p>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-gray-500">
            {concept.season && <span className="rounded bg-gray-100 px-1.5 py-0.5">{concept.season}</span>}
            {concept.defaultSectionTitle && (
              <span className="inline-flex items-center gap-0.5 rounded bg-gray-100 px-1.5 py-0.5"><Store className="h-2.5 w-2.5" />{concept.defaultSectionTitle}</span>
            )}
          </div>
        </div>
      </div>

      {concept.description && <p className="mb-2 line-clamp-2 text-xs text-gray-500">{concept.description}</p>}

      {/* Keyword onizleme */}
      <div className="mb-3 flex flex-wrap gap-1">
        {concept.keywords.slice(0, 5).map((k, i) => (
          <span key={i} className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] text-indigo-600">{k}</span>
        ))}
        {total > 5 && <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-400">+{total - 5}</span>}
      </div>

      <div className="mt-auto flex items-center justify-between border-t border-gray-100 pt-2">
        <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
          <TrendingUp className="h-3 w-3" /> {concept.usageCount}x kullanıldı
        </span>
        <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          <button onClick={onDuplicate} title="Kopyala" className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"><Copy className="h-3.5 w-3.5" /></button>
          <button onClick={onEdit} title="Düzenle" className="rounded px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50">Düzenle</button>
          <button onClick={onDelete} title="Sil" className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>
    </div>
  );
}

// ======== Page ========
export default function ConceptsPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ConceptForm | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const { data: concepts = [], isLoading } = useQuery({ queryKey: ["concepts"], queryFn: fetchConcepts });
  const { data: sections = [] } = useQuery({ queryKey: ["etsy-shop-sections"], queryFn: fetchShopSections, staleTime: 5 * 60_000 });

  const saveM = useMutation({
    mutationFn: async (form: ConceptForm) => {
      const isUpdate = !!editingId;
      const res = await fetch(`${API_BASE}/api/concepts${isUpdate ? `/${editingId}` : ""}`, {
        method: isUpdate ? "PATCH" : "POST",
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Kaydedilemedi");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["concepts"] });
      setEditing(null);
      setEditingId(null);
    },
  });

  const deleteM = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE}/api/concepts/${id}`, { method: "DELETE", headers: authHeaders() });
      if (!res.ok) throw new Error("Silinemedi");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["concepts"] }),
  });

  const duplicateM = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE}/api/concepts/${id}/duplicate`, { method: "POST", headers: authHeaders() });
      if (!res.ok) throw new Error("Kopyalanamadı");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["concepts"] }),
  });

  // Etsy'de yeni magaza bolumu olustur (shops_w scope) — olusturulani dondurur
  async function createShopSection(title: string): Promise<ShopSection | null> {
    const res = await fetch(`${API_BASE}/api/etsy/listings/shop-sections/create`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ title }),
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.error || "Bölüm oluşturulamadı");
    }
    const json = await res.json();
    await queryClient.invalidateQueries({ queryKey: ["etsy-shop-sections"] });
    return (json.data ?? null) as ShopSection | null;
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return concepts;
    return concepts.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.description ?? "").toLowerCase().includes(q) ||
        [...c.keywords, ...c.audiences].some((k) => k.toLowerCase().includes(q)),
    );
  }, [concepts, search]);

  function openNew(preset?: Partial<ConceptForm>) {
    setEditingId(null);
    setEditing({ ...EMPTY_FORM, ...preset });
  }

  function openEdit(c: Concept) {
    setEditingId(c.id);
    const { id, usageCount, createdAt, updatedAt, ...form } = c;
    void id; void usageCount; void createdAt; void updatedAt;
    setEditing(form);
  }

  const usablePresets = PRESETS.filter((p) => !concepts.some((c) => c.name.toLowerCase() === p.name.toLowerCase()));

  return (
    <div className="mx-auto max-w-6xl px-6 py-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
            <Layers className="h-6 w-6 text-indigo-600" /> Konseptler
          </h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Tema bazlı anahtar kelime havuzları. Listeleme üretiminde bir konsept seçtiğinde AI, buradaki kelimelerden tasarıma uygun olanları seçer.
          </p>
        </div>
        <button onClick={() => openNew()} className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">
          <Plus className="h-4 w-4" /> Yeni Konsept
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Konsept ara…"
          className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
      </div>

      {/* Preset library */}
      {usablePresets.length > 0 && (
        <div className="mb-6 rounded-xl border border-dashed border-gray-200 bg-gray-50/60 p-4">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-gray-600"><Sparkles className="h-3.5 w-3.5 text-amber-500" /> Hazır konseptlerden başla</p>
          <div className="flex flex-wrap gap-2">
            {usablePresets.map((p) => (
              <button key={p.name} onClick={() => openNew(p)}
                className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 transition-colors hover:border-indigo-300 hover:bg-indigo-50">
                <span>{p.emoji}</span> {p.name} <Plus className="h-3 w-3 text-gray-400" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20 text-gray-400"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Yükleniyor…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 py-16 text-center">
          <Layers className="mx-auto mb-3 h-10 w-10 text-gray-300" />
          <p className="text-sm text-gray-500">{search ? "Eşleşen konsept yok." : "Henüz konsept yok. Yukarıdan bir hazır konsept ekle veya yeni oluştur."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((c) => (
            <ConceptCard
              key={c.id}
              concept={c}
              onEdit={() => openEdit(c)}
              onDelete={() => { if (confirm(`"${c.name}" konsepti silinsin mi?`)) deleteM.mutate(c.id); }}
              onDuplicate={() => duplicateM.mutate(c.id)}
            />
          ))}
        </div>
      )}

      {/* Editor */}
      {editing && (
        <ConceptEditor
          initial={editing}
          sections={sections}
          saving={saveM.isPending}
          onCancel={() => { setEditing(null); setEditingId(null); }}
          onSave={(form) => saveM.mutate(form)}
          onCreateSection={createShopSection}
        />
      )}
    </div>
  );
}
